/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import store from "reducers/Store";
import * as ActionTypes from "constants/ActionTypes";
import { assetLoaded, assetLoadFailed } from "actions/AssetActions";
import { Asset, AssetId, AssetSet } from "types/Asset";
import { noticeError } from "utilities/noticeError";
import { fetchExpiringUrl } from "components/EnhancedSpeech/Utilities";
import gatewayFetch from "../utilities/gatewayFetch";
import { ProjectLoadAnalytics } from "utilities/projectLoadAnalytics";

export default class AudioBufferCache {
  _audioBufferPromises: Record<AssetId, Promise<AudioBuffer | null>> = {};
  _context = new OfflineAudioContext(1, 44100 * 60, 44100);

  getAssetAudioBuffer(
    assetId: AssetId
  ): Promise<AudioBuffer | null> | undefined {
    return this._audioBufferPromises[assetId];
  }

  async loadRemoteAssets(assets: AssetSet): Promise<unknown> {
    const buffers = await Promise.all(
      Object.entries(assets).map(([assetId, asset]) => {
        if (!asset.url) {
          return;
        }
        // TODO: Handle rejected / !status.ok promises for retry
        if (!this._audioBufferPromises[assetId]) {
          this._audioBufferPromises[assetId] = this._loadRemoteAsset(
            assetId,
            asset
          );
        }
        return this._audioBufferPromises[assetId];
      })
    );

    store.dispatch({
      type: ActionTypes.SET_INITIAL_ASSETS_LOADED,
    });

    return buffers;
    // TODO: Remove unused assets
    // TODO: dispatch(removeAssetBlobUrl)
  }

  loadLocalAsset(assetId: AssetId, blob: Blob): Promise<AudioBuffer | null> {
    if (!this._audioBufferPromises[assetId]) {
      const audioBufferPromise = this._loadLocalAsset(assetId, blob);
      if (audioBufferPromise) {
        this._audioBufferPromises[assetId] = audioBufferPromise;
      }
    }
    return this._audioBufferPromises[assetId];
  }

  free(assetId: AssetId): void {
    delete this._audioBufferPromises[assetId];
  }

  reset(): void {
    this._audioBufferPromises = {};
  }

  private async _fetchWithRetryAndRefreshUrl(
    assetId: AssetId,
    initialUrl: string,
    retries: number = 3
  ): Promise<Response> {
    let url = initialUrl;
    let lastError: Error | null = null;
    const state = store.getState();
    const projectId = state.project.id;
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const presignedUrl = await fetchExpiringUrl(url);
        const response = await fetch(presignedUrl);
        // If the response is a 403, it's likely an ExpiredToken error
        // If the response is a 200, it's possibly a weird case where the token is expired somehow
        if (!response.ok || attempt === retries - 1) {
          // there is no good way to get the contentlength from the response if we get a content mismatch error on a 200 response
          if (response.status === 403 || attempt === retries - 1) {
            console.warn(
              `Attempt ${
                attempt + 1
              }: ExpiredToken detected for asset ${assetId}. Refreshing URL...`
            );

            const refreshedUrlResponse = await gatewayFetch(
              `/api/v1/projects/${projectId}/tracks/${assetId}`,
              {
                method: "GET",
                headers: { "Content-Type": "application/json" },
              },
              { reportErrors: false }
            );
            if (!refreshedUrlResponse.ok) {
              throw new Error("Failed to refresh URL");
            }
            const refreshedData = await refreshedUrlResponse.json();
            url = refreshedData.url; // Update the URL with the refreshed one

            continue; // Retry fetching the asset with the refreshed URL
          } else {
            throw new Error(`HTTP error: ${response.status}`);
          }
        }
        return response;
      } catch (error) {
        console.debug(
          `Attempt ${attempt + 1} to fetch asset ${assetId} failed:`,
          error
        );
        lastError = error as Error;
        // Retry fetching the asset if we have more attempts left
        if (attempt < retries - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }

    // TODO: throw(null)??
    throw (
      lastError ?? new Error("Unknown error occurred while fetching asset.")
    );
  }

  private async _loadRemoteAsset(
    assetId: AssetId,
    asset: Asset
  ): Promise<AudioBuffer | null> {
    const projectLoadAnalytics = ProjectLoadAnalytics.getInstance();
    projectLoadAnalytics.recordAssetLoadStart(assetId);

    try {
      if (!asset || !asset.url) {
        console.warn("Asset data missing:", assetId);
        return null;
      }
      console.time(`Download: ${assetId}`);

      const response = await this._fetchWithRetryAndRefreshUrl(
        assetId,
        asset.url
      );

      if (!response.ok) {
        console.debug("Failed to load remote asset:", assetId, response.status);
        noticeError("Unable to load remote asset", {
          assetId,
          statusCode: response.status,
        });
        return null;
      }

      projectLoadAnalytics.setAssetContentLength(
        assetId,
        parseInt(response.headers?.get("content-length") ?? "0")
      );

      console.timeEnd(`Download: ${assetId}`);
      console.time(`Get blob: ${assetId}`);
      const blob = await response.blob();

      console.timeEnd(`Get blob: ${assetId}`);
      const buffer = await this._loadLocalAsset(assetId, blob);

      if (buffer != null) {
        projectLoadAnalytics.setAssetDuration(assetId, buffer.duration);
      }

      return buffer;
    } catch (error) {
      console.debug("Error loading remote asset:", assetId, error);
      noticeError("Error loading remote asset", { assetId, error });
      store.dispatch(assetLoadFailed(assetId));
      throw error;
    } finally {
      projectLoadAnalytics.recordAssetLoadEnd(assetId);
    }
  }
  async _loadLocalAsset(
    assetId: AssetId,
    blob: Blob
  ): Promise<AudioBuffer | null> {
    console.time(`Get blob array buffer: ${assetId}`);
    const arrayBuffer = await blob.arrayBuffer();
    console.timeEnd(`Get blob array buffer: ${assetId}`);
    try {
      console.time(`Decode: ${assetId}`);
      newrelic.addPageAction("AUDIO_ASSET_DECODE_STARTED", {
        assetId,
        deviceMemory: window.navigator.deviceMemory,
      });
      const audioBuffer = await this._context.decodeAudioData(arrayBuffer);
      newrelic.addPageAction("AUDIO_ASSET_DECODE_COMPLETED", {
        assetId,
        deviceMemory: window.navigator.deviceMemory,
      });
      console.timeEnd(`Decode: ${assetId}`);
      store.dispatch(assetLoaded(assetId));
      return audioBuffer;
    } catch (error) {
      console.error("Error decoding audio data:", assetId, error);
      newrelic.noticeError(error, { assetId });
      store.dispatch(assetLoadFailed(assetId));
      return null;
    }
  }
}
